import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { type User } from "../types";
import { useAuth } from "../contexts/AuthContext";
import { useConfirmUnfollow } from "../hooks/useConfirmUnfollow";
import { XIcon, SearchIcon, UsersIcon } from "./icons/FeatherIcons";
import { motion } from "framer-motion";
import { fallbackImage } from "../utils/getCachedImage";
import { useCachedImage } from "../hooks/useCachedImage";

import { crashlytics } from "../services/firebase";
import { CommonFunctions } from "@/utils/commonUtils";
interface UserSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
  if (e.currentTarget.src !== fallbackImage) {
    e.currentTarget.src = fallbackImage;
  }
};

const UserSearchItem: React.FC<{ user: User; onClose: () => void }> = ({
  user,
  onClose,
}) => {
  const { currentUser, requestFollow, cancelFollowRequest } = useAuth();
  const { confirmUnfollow, ConfirmUnfollowModal } = useConfirmUnfollow();
  const navigate = useNavigate();

  if (!currentUser) return null;

  const isFollowing = currentUser.following?.includes(user.id);
  const hasRequested = currentUser.requestedFollowing?.includes(user.id);
  const isCurrentUser = currentUser.id === user.id;

  const handleFollowAction = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!currentUser || user.id === currentUser.id) return;
    if (isFollowing) {
      confirmUnfollow(user.id);
    } else if (hasRequested) {
      cancelFollowRequest(user.id);
    } else {
      requestFollow(user.id);
    }
  };

  const handleNavigate = () => {
    onClose();
    navigate(`/profile/${user.id}`);
  };

  const renderFollowButton = () => {
    if (isCurrentUser) return null;

    let text = "Follow";
    let className = "bg-primary text-white text-xs px-3 py-1 rounded-full";

    if (isFollowing) {
      text = "Following";
      className =
        "border border-gray-300 bg-white text-gray-600 text-xs px-3 py-1 rounded-full";
    } else if (hasRequested) {
      text = "Requested";
      className =
        "border border-gray-200 bg-gray-100 text-gray-500 text-xs px-3 py-1 rounded-full";
    }

    return (
      <button onClick={handleFollowAction} className={className}>
        {text}
      </button>
    );
  };

  return (
    <>
      <div
        onClick={handleNavigate}
        className="flex items-center p-3 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
      >
        {(() => {
          const profileImage = useCachedImage(
            user.profileImageUrl,
            user.username,
            user.id,
          );
          return (
            <img
              loading="lazy"
              onError={handleImageError}
              src={profileImage}
              alt={user.username}
              className="w-12 h-12 rounded-2xl"
            />
          );
        })()}
        <div className="ml-3 flex-1 overflow-hidden">
          <div className="flex items-center space-x-2">
            <h3 className="text-sm font-semibold text-gray-900 truncate">
              {user.username}
            </h3>
            {user.isAccountVerifiedForBadge && (
              <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                <span className="text-white text-xs">✓</span>
              </div>
            )}
          </div>
          <p className="text-xs text-gray-500 truncate">
            {CommonFunctions.calculateAge(user.dateOfBirth)} • {user.location}
          </p>
          {user.bio && (
            <p className="text-xs text-gray-400 truncate mt-1">{user.bio}</p>
          )}
        </div>
        <div className="ml-2">{renderFollowButton()}</div>
      </div>
      {ConfirmUnfollowModal}
    </>
  );
};

const UserSearchModal: React.FC<UserSearchModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { searchUsers, currentUser } = useAuth();
  const modalRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [lastDoc, setLastDoc] = useState<any>(null);
  const [searchTimeoutId, setSearchTimeoutId] = useState<NodeJS.Timeout | null>(
    null,
  );

  // Load initial users when modal opens
  useEffect(() => {
    if (isOpen) {
      loadInitialUsers();
      // Focus on search input when modal opens
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    } else {
      resetState();
    }
  }, [isOpen]);

  // Handle search query changes with debouncing
  useEffect(() => {
    if (searchTimeoutId) {
      clearTimeout(searchTimeoutId);
    }

    const timeoutId = setTimeout(() => {
      handleSearch();
    }, 300); // Debounce search by 300ms

    setSearchTimeoutId(timeoutId);

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [searchQuery]);

  const resetState = () => {
    setSearchQuery("");
    setUsers([]);
    setLastDoc(null);
    setHasMore(true);
    setIsLoading(false);
    setIsLoadingMore(false);
  };

  const loadInitialUsers = async () => {
    setIsLoading(true);
    try {
      const result = await searchUsers("", null, 20);
      // Exclude current user from search results
      const filteredUsers = result.users.filter(
        (user) => user.id !== currentUser?.id,
      );
      setUsers(filteredUsers);
      setLastDoc(result.lastDoc);
      setHasMore(result.hasMore);
    } catch (error) {
      crashlytics.logError(
        `Error loading initial users: ${JSON.stringify(error?.message)}`,
        "UserSearchModal.tsx",
        error,
      );
      setUsers([]);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async () => {
    setIsLoading(true);
    setLastDoc(null);
    setHasMore(true);

    try {
      const result = await searchUsers(searchQuery, null, 20);
      // Exclude current user from search results
      const filteredUsers = result.users.filter(
        (user) => user.id !== currentUser?.id,
      );
      setUsers(filteredUsers);
      setLastDoc(result.lastDoc);
      setHasMore(result.hasMore);
    } catch (error) {
      crashlytics.logError(
        `Error searching users searchQuery=${searchQuery} Error=${JSON.stringify(error?.message)}`,
        "UserSearchModal.tsx",
        error,
      );
      setUsers([]);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMoreUsers = async () => {
    if (!hasMore || isLoadingMore || !lastDoc) return;

    setIsLoadingMore(true);
    try {
      const result = await searchUsers(searchQuery, lastDoc, 20);
      // Exclude current user from search results
      const filteredUsers = result.users.filter(
        (user) => user.id !== currentUser?.id,
      );
      setUsers((prev) => [...prev, ...filteredUsers]);
      setLastDoc(result.lastDoc);
      setHasMore(result.hasMore);
    } catch (error) {
      crashlytics.logError(
        `Error loading more users searchQuery=${searchQuery} Error=${JSON.stringify(error?.message)}`,
        "UserSearchModal.tsx",
      );
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Infinite scroll functionality
  useEffect(() => {
    const trigger = loadMoreTriggerRef.current;
    if (!trigger) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMore && !isLoadingMore) {
          loadMoreUsers();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(trigger);
    return () => observer.disconnect();
  }, [hasMore, isLoadingMore, lastDoc, searchQuery]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "auto";
    };
  }, [isOpen, onClose]);

  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 pt-20"
      onClick={handleBackdropClick}
    >
      <div
        ref={modalRef}
        className="bg-white rounded-2xl w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
          <h2 className="text-lg font-bold text-gray-900">Search Users</h2>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-100 transition-colors"
          >
            <XIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Search Input */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              autoComplete="off" // Disable autofill
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by username, location, or interests..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
        </div>

        {/* Search Results */}
        <div className="flex-1 overflow-y-auto">
          {isLoading && users.length === 0 ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : users.length > 0 ? (
            <>
              <div className="py-2">
                {users.map((user) => (
                  <UserSearchItem key={user.id} user={user} onClose={onClose} />
                ))}
              </div>

              {/* Infinite scroll trigger */}
              <div ref={loadMoreTriggerRef} className="w-full py-2">
                {isLoadingMore && (
                  <div className="flex items-center justify-center py-4">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 1,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                      className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full"
                    />
                    <span className="ml-2 text-sm text-gray-500">
                      Loading more users...
                    </span>
                  </div>
                )}

                {!hasMore && users.length > 0 && (
                  <div className="text-center py-4">
                    <p className="text-sm text-gray-400">
                      You've seen all users!
                    </p>
                  </div>
                )}
              </div>
            </>
          ) : searchQuery.trim() !== "" ? (
            <div className="py-12 px-4 text-center">
              <SearchIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">
                No users found matching "{searchQuery}"
              </p>
            </div>
          ) : (
            <div className="py-12 px-4 text-center">
              <UsersIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">Loading users...</p>
            </div>
          )}
        </div>

        {/* Footer with user count */}
        {!isLoading && users.length > 0 && (
          <div className="px-4 py-2 bg-gray-50 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              {searchQuery.trim() === ""
                ? `${users.length} users loaded`
                : `${users.length} results`}
              {hasMore && " • Scroll for more"}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserSearchModal;
